/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import Cookies from 'universal-cookie';

export interface ICategoryLevelNamesData {
    categoryLevelName?: string;
}
export class GetUnbxdRecommendationsDataInput implements Msdyn365.IActionInput {
    public pageType?: string | undefined;
    public widgetNumber?: string | undefined;
    public productID?: string | undefined;
    public catLevels?: ICategoryLevelNamesData[] | undefined;
    public cartItemIDs?: (string | undefined)[] | undefined;
    private currentCategories: string;

    constructor(pageType?: string | undefined, widgetNumber?: string | undefined, productID?: string | undefined, catLevels?: ICategoryLevelNamesData[] | undefined, cartItemIDs?: (string | undefined)[] | undefined) {
        this.pageType = pageType;
        this.widgetNumber = widgetNumber;
        this.productID = productID;
        this.catLevels = catLevels;
        this.cartItemIDs = cartItemIDs;
        this.currentCategories = '';
        catLevels?.forEach(catLevel => {
            this.currentCategories = catLevel?.categoryLevelName ? this.currentCategories.concat(`${catLevel?.categoryLevelName}-`) : this.currentCategories;
        });
    }

    public getCacheKey = () => `Unbxd-recommendation-data-${this.pageType}-${this.widgetNumber}-${this.currentCategories}`;
    public getCacheObjectType = () => 'Unbxd-recommendation-data';
    public dataCacheType = (): Msdyn365.CacheType => 'request';
}

/**
 * Use this function to call your action and process the results as needed
 */
async function getUnbxdRecommendationData(input: GetUnbxdRecommendationsDataInput, ctx: Msdyn365.IActionContext): Promise<any> {
    const cookies = new Cookies();
    const unbxdUid = cookies.get('unbxd.userId');
    const pageType = input?.pageType ? input.pageType.toUpperCase() : "";
    const productID = input?.productID ? input.productID : "";
    const catLevels = input?.catLevels ? input.catLevels : [];
    const cartItemIDs = input?.cartItemIDs ? input.cartItemIDs : [];
    const unbxdApiKey = ctx.requestContext.app.config.unbxdApiKey;
    const unbxdSiteKey = ctx.requestContext.app.config.unbxdSiteKey;

    if (!pageType) {
        return null;
    }
    try {
        let unbxdUrl = "";
        switch (pageType) {
            case "HOME":
                unbxdUrl = `https://recommendations.unbxd.io/v2.0/${unbxdApiKey}/${unbxdSiteKey}/items?pageType=${pageType}&uid=${unbxdUid}`;
                break;
            case "CATEGORY":
                const catnames = catLevels.map((catName, index) => {
                    return `&catlevel${index + 1}Name=${catName ? catName.categoryLevelName : ''}`;
                }).join('');

                unbxdUrl = `https://recommendations.unbxd.io/v2.0/${unbxdApiKey}/${unbxdSiteKey}/items?pageType=${pageType}${catnames}&uid=${unbxdUid}`;
                break;
            case "PRODUCT":
                unbxdUrl = `https://recommendations.unbxd.io/v2.0/${unbxdApiKey}/${unbxdSiteKey}/items?pageType=${pageType}&id=${productID}&uid=${unbxdUid}`;
                break;
            case "CART":
                const productitemIds = cartItemIDs.map((itemId) => {
                    return `&id=${itemId}`;
                }).join('');

                unbxdUrl = `https://recommendations.unbxd.io/v2.0/${unbxdApiKey}/${unbxdSiteKey}/items?pageType=${pageType}${productitemIds}&uid=${unbxdUid}`;
                break;
            default:
                unbxdUrl = '';
        }
        const response = await Msdyn365.sendRequest<any>(unbxdUrl, 'get');
        if (response.data && response.headers) {
            const recsData={
                "unbxdRequestId":response.headers["x-request-id"]? response.headers["x-request-id"]:'',
                "unbxdRecs":response.data[input.widgetNumber!.toLowerCase()]
            };
            return recsData;
        }
        else { return null; }
    } catch (e) {
        return null;
    }
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<{}>>getUnbxdRecommendationData,
    id: 'getUnbxdRecommendationData'
});
